/**
 * Initialize state sets all application state at once based on api/identity/info.
 *
 * The fetch is backed by useAsyncData (key "identity-info"), so the SSR result is
 * serialized into the Nuxt payload and reused on hydration rather than re-fetched.
 * Call initializeState() in the top scope of app.vue (runs once on the server, reuses
 * the payload on the client). After a successful login, call initializeState({ force:
 * true }) to re-fetch the now-authenticated identity.
 */
export default function useInitializeState() {
    const { $api } = useNuxtApp();
    const isAuthenticated = useStateIsAuthenticated();
    const userInfo = useStateUserInfo();
    const locationInfo = useStateLocationInfo();
    const regionInfo = useStateRegionInfo();
    const currencyInfo = useStateCurrencyInfo();
    const featureFlags = useStateFeatureFlags();
    const searchText = useStateSearchText();
    const isBot = useStateIsBot();
    const hasSpecialPricing = useStateHasSpecialPricing();

    // Set by DataDome upstream; only present on the server request, so read it once here.
    if (import.meta.server) {
        isBot.value = useRequestHeaders(["x-datadome-isbot"])["x-datadome-isbot"] === "1";
    }

    // Determine if the connection is secure, respecting x-forwarded-proto from the load balancer
    const requestUrl = useRequestURL();
    const isSecure = requestUrl.protocol === "https:";

    // ASP.NET Core makes use of 2 tokens for XSRF prevention
    // the persistent cookie token "eciarvt" and the request token "XSRF-TOKEN"
    const eciarvtCookie = useCookie("eciarvt", { httpOnly: false, sameSite: "strict", path: "/", secure: isSecure });
    const xsrfCookie = useCookie("XSRF-TOKEN", { httpOnly: false, sameSite: "strict", path: "/", secure: isSecure });
    const xsrfToken = useState("xsrfToken", () => xsrfCookie.value);

    // Fetch identity/info through useAsyncData so the SSR result is serialized into the
    // Nuxt payload and reused on hydration instead of being fetched a second time on the
    // client. immediate:false lets initializeState() drive when the fetch happens (once on
    // the server; skipped on the client when the payload already holds the data).
    const { data: accountData, refresh } = useAsyncData<AccountData>(
        "identity-info",
        () => $api<AccountData>("identity/info"),
        { immediate: false }
    );

    function applyAccountData(data: AccountData) {
        if (data.XSRFCookieToken) {
            eciarvtCookie.value = data.XSRFCookieToken;
        }

        xsrfCookie.value = data.XSRFRequestToken;
        xsrfToken.value = data.XSRFRequestToken;
        isAuthenticated.value = data.IsAuthenticated ?? false;
        userInfo.value = data.User;
        locationInfo.value = data.Location;

        featureFlags.value = data.Features;
        searchText.value = "";
        regionInfo.value = data.Regions || [];
        currencyInfo.value = data.Currencies || [];
        hasSpecialPricing.value = data.HasSpecialPricing ?? false;
    }

    return async function initializeState({ force = false } = {}) {
        // Force a re-fetch after events that change identity (e.g. login). Otherwise fetch
        // only when the payload has no cached data yet (i.e. the initial server render).
        if (force || !accountData.value) {
            await refresh();
        }

        if (accountData.value) {
            applyAccountData(accountData.value);
        }

        return {
            isAuthenticated,
            userInfo,
            locationInfo,
            regionInfo,
            currencyInfo,
            featureFlags,
            searchText,
            isBot,
            hasSpecialPricing,
            accountData: accountData.value,
        };
    };
}
